import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  House,
  CalendarBlank,
  CalendarDots,
  GearSix,
  ListChecks,
  SignOut,
  Lightning,
  List,
  X,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/", icon: House, label: "Panel", testId: "nav-dashboard" },
  { to: "/semana", icon: CalendarDots, label: "Semana", testId: "nav-week" },
  { to: "/horarios", icon: CalendarBlank, label: "Horarios", testId: "nav-schedule" },
  { to: "/logs", icon: ListChecks, label: "Actividad", testId: "nav-logs" },
  { to: "/config", icon: GearSix, label: "Config", testId: "nav-config" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {/* MOBILE TOP BAR */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0A0A0A] border-b border-zinc-900 flex items-center justify-between px-4 pb-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-[#CCFF00] flex items-center justify-center">
            <Lightning size={18} weight="fill" className="text-black" />
          </div>
          <div className="font-display font-black text-base leading-none tracking-tight">LUCES DON TELLO</div>
        </div>
        <button
          data-testid="mobile-menu-button"
          onClick={() => setDrawerOpen(true)}
          className="text-zinc-300 p-2"
          aria-label="Abrir menú"
        >
          <List size={24} weight="bold" />
        </button>
      </div>

      {/* MOBILE DRAWER (slide-in menu) */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        >
          <aside
            className="absolute right-0 top-0 h-full w-72 bg-[#0A0A0A] border-l border-zinc-800 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-[#CCFF00] flex items-center justify-center">
                  <Lightning size={18} weight="fill" className="text-black" />
                </div>
                <div className="font-display font-black text-base leading-none tracking-tight">LUCES DON TELLO</div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="text-zinc-400 p-1"
                aria-label="Cerrar menú"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-${item.testId}`}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded text-sm font-medium transition-all ${
                      isActive
                        ? "bg-[#CCFF00] text-black"
                        : "text-zinc-300 hover:text-white hover:bg-zinc-900"
                    }`
                  }
                >
                  <item.icon size={20} weight="bold" />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="p-3 border-t border-zinc-900">
              <div className="px-3 py-2 mb-2">
                <div className="text-[10px] text-zinc-600 tracking-wider uppercase font-mono">Sesión</div>
                <div className="text-xs text-zinc-300 truncate">{user?.email}</div>
              </div>
              <button
                data-testid="mobile-logout-button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-zinc-400 hover:text-red-400 hover:bg-zinc-900 transition-colors"
              >
                <SignOut size={16} weight="bold" />
                Cerrar sesión
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-60 bg-[#0A0A0A] border-r border-zinc-900 flex-col z-20">
        <div className="px-5 py-6 border-b border-zinc-900">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded bg-[#CCFF00] flex items-center justify-center">
              <Lightning size={20} weight="fill" className="text-black" />
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">LUCES DON TELLO</div>
              <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase font-mono mt-0.5">
                v1.0
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[#CCFF00] text-black"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`
              }
            >
              <item.icon size={18} weight="bold" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-900">
          <div className="px-3 py-2 mb-2">
            <div className="text-[10px] text-zinc-600 tracking-wider uppercase font-mono">Sesión</div>
            <div className="text-xs text-zinc-300 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-zinc-400 hover:text-red-400 hover:bg-zinc-900 transition-colors"
          >
            <SignOut size={16} weight="bold" />
            Cerrar sesión
          </button>
        </div>
      </aside>
    </>
  );
}
